import { Column, getColumnHeaderText } from '../components/DataTable/DataTable';
import { exportToExcel } from '../components/exports/exportToExcel';
import { exportToPDF } from '../utils/exportToPdf';

export type ExportFormat = 'csv' | 'excel' | 'pdf';

export interface ExportData {
  sortedData: any[];
  tableColumns: Column<any>[];
  entityName: string;
  exportFileName: string;
  formatDate?: (dateString?: string) => string;
}

export interface ExportOptions {
  format: ExportFormat;
  data: ExportData;
}

/**
 * Export data to the specified format
 * @param options - Export options including format and data
 * @returns Promise that resolves when export is complete
 */
export const exportData = async (options: ExportOptions): Promise<void> => {
  const { format, data } = options;

  try {
    switch (format) {
      case 'csv':
        await exportToCSV(data);
        break;
      case 'excel':
        await exportToExcel(data);
        break;
      case 'pdf':
        await exportToPDF(data);
        break;
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  } catch (error: any) {
    console.error(`Export to ${format} failed:`, error);
    throw error;
  }
};

/**
 * Export data to CSV format
 * @param exportData - Data to export
 */
export const exportToCSV = async (exportData: ExportData): Promise<void> => {
  const { sortedData, tableColumns, entityName, exportFileName, formatDate } = exportData;

  // Filter out actions column
  const columnsToExport = tableColumns.filter((col) => col.key !== 'actions');

  // Get headers
  const headers = columnsToExport.map(
    (col) => getColumnHeaderText(col.header, String(col.key)),
  );

  // Helper function to format array values
  const formatArrayValue = (value: any, column: Column<any>, row: any): string => {
    if (!value && !(column as any).displayField) return '';

    let arrayValue = value;
    if ((column as any).displayField && row && row[(column as any).displayField]) {
      arrayValue = row[(column as any).displayField];
    }

    if (!Array.isArray(arrayValue)) {
      if (typeof arrayValue === 'string' && arrayValue.trim().startsWith('[')) {
        try {
          arrayValue = JSON.parse(arrayValue);
        } catch {
          return String(value || '');
        }
      } else {
        return String(value || '');
      }
    }

    if (arrayValue.length === 0) return '';

    if (arrayValue.length > 0 && typeof arrayValue[0] === 'object') {
      const names = arrayValue
        .map((item: any) => {
          return (
            item.userDisplayName ||
            item.name ||
            item.title ||
            item.label ||
            (item.userFirstName && item.userLastName
              ? `${item.userFirstName} ${item.userLastName}`
              : '')
          );
        })
        .filter((name: string) => name);

      return names.join(', ');
    }

    return arrayValue.join(', ');
  };

  // Helper function to format dates
  const formatDateValue = (value: any, column: Column<any>): string => {
    if (!value) return '';
    try {
      const dateStr = String(value);
      if (dateStr.includes('T') || dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
        if (formatDate) {
          return formatDate(value instanceof Date ? value.toISOString() : value);
        }
        return new Date(value).toLocaleDateString();
      }
      return String(value);
    } catch {
      return String(value || '');
    }
  };

  // Build CSV rows
  const csvRows: string[] = [];

  // Add title row
  csvRows.push(`${entityName} Export`);

  // Add timestamp
  csvRows.push(`Generated: ${new Date().toLocaleString()}`);

  // Empty row
  csvRows.push('');

  // Add headers
  csvRows.push(headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(','));

  // Add data rows
  sortedData.forEach((row) => {
    const values = columnsToExport.map((col) => {
      let value = row[String(col.key)];

      // Check displayField
      if ((value === undefined || value === null) && (col as any).displayField && row[(col as any).displayField] !== undefined) {
        value = row[(col as any).displayField];
      }

      if (value === undefined || value === null) {
        value = '';
      }

      // Format dates
      if (col.render || (col as any).cellRenderer === 'date') {
        value = formatDateValue(value, col);
      }
      // Format arrays
      else if ((col as any).cellRenderer === 'array' || Array.isArray(value)) {
        value = formatArrayValue(value, col, row);
      }
      // Handle objects
      else if (typeof value === 'object' && value !== null) {
        if (value.name || value.title || value.label) {
          value = value.name || value.title || value.label;
        } else {
          value = JSON.stringify(value);
        }
      }

      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      const valueStr = String(value);
      if (valueStr.includes(',') || valueStr.includes('\n') || valueStr.includes('"')) {
        return `"${valueStr.replace(/"/g, '""')}"`;
      }

      return valueStr;
    });

    csvRows.push(values.join(','));
  });

  // Create CSV content with UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';
  const csvContent = BOM + csvRows.join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8',
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${exportFileName}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
